import { motion } from 'motion/react';
import React, { useLayoutEffect, useRef, useState } from 'react';

import { Dialog, DialogContent, cn } from '@column/ui';

import { useStepUpStore } from '~/stores/StepUp';

export const EASE = [0.25, 0.1, 0.25, 1] as const;
export const DIALOG_WIDEN_MS = 300;
export const BACKGROUND_NOTICE = 'You may close this window, processing will continue in the background.';
const SETTLE_MS = 300;

interface AnimatedHeightProps {
  children: React.ReactNode;
  className?: string;
  freeze?: boolean;
}

export const AnimatedHeight: React.FC<AnimatedHeightProps> = ({ children, className, freeze = false }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const freezeRef = useRef(freeze);
  freezeRef.current = freeze;
  const [height, setHeight] = useState<number | 'auto'>('auto');
  const [isSettled, setIsSettled] = useState(false);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) return undefined;
    setHeight(element.offsetHeight);
    const observer = new ResizeObserver(() => {
      const next = element.offsetHeight;
      setHeight((current) => (freezeRef.current && current !== 'auto' && next < current ? current : next));
    });
    observer.observe(element);
    const settle = window.setTimeout(() => setIsSettled(true), SETTLE_MS);
    return () => {
      window.clearTimeout(settle);
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (!freeze && contentRef.current) setHeight(contentRef.current.offsetHeight);
  }, [freeze]);

  return (
    <motion.div
      className="-mx-6 -mt-2 box-content min-w-0 overflow-hidden px-6 py-2"
      initial={false}
      animate={{ height }}
      transition={{ duration: isSettled ? 0.2 : 0, ease: EASE }}
    >
      <div ref={contentRef} className={cn('min-w-0', className)}>
        {children}
      </div>
    </motion.div>
  );
};

interface ReviewDialogProps {
  isOpen: boolean;
  isWide?: boolean;
  onClose: () => void;
  className?: string;
  showCloseButton?: boolean;
  children: React.ReactNode;
}

export const ReviewDialog: React.FC<ReviewDialogProps> = ({
  isOpen,
  isWide = false,
  onClose,
  className,
  showCloseButton = true,
  children,
}) => {
  const isStepUpOpen = useStepUpStore((state) => state.challenge !== null);
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isStepUpOpen && onClose()}>
      <DialogContent
        showCloseButton={showCloseButton}
        className={cn('transition-[max-width] ease-out', className, isWide && 'sm:max-w-3xl')}
        style={{ transitionDuration: `${DIALOG_WIDEN_MS}ms` }}
        onInteractOutside={(event) => isStepUpOpen && event.preventDefault()}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
